import { Box, SwipeableDrawer } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useRef, useEffect, useState } from 'react';

import { OpenedReserveWidget } from './openedReserveWidget';
import { ReserveHorizontalCard } from './reserveHorizontalCard';

const useStyles = makeStyles({
  scrollableContent: {
    overflowY: 'auto',
    maxHeight: 'calc(85vh - env(safe-area-inset-bottom) - 60px)',
    // Минималистичный скроллбар для Firefox
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(0, 0, 0, 0.15) transparent',
    // Минималистичный скроллбар для Chrome/Safari
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0, 0, 0, 0.15)',
      borderRadius: '2px',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.25)',
      },
    },
  },
});

// Пустая функция для onOpen, так как открытие происходит только по клику
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

interface ReservesHorizontalListProps {
  reservesView: Array<GM.ReservesSchemas.ScheduleReserve>;
  tablesSchedule: GM.ReservesSchemas.TablesSchedule;
  restaurantTz?: string;
  selectedReserveId?: number;
  onReserveClick?: (reserveId: number) => void;
  onCancel?: (reserve: GM.ReservesSchemas.ScheduleReserve) => void;
  isCancelDisabled?: boolean;
  isLoading?: boolean;
}

export const ReservesHorizontalList: React.FC<ReservesHorizontalListProps> = ({
  reservesView,
  tablesSchedule,
  restaurantTz,
  selectedReserveId,
  onReserveClick,
  onCancel,
  isCancelDisabled = false,
  isLoading,
}) => {
  const classes = useStyles();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [openedReserveId, setOpenedReserveId] = useState<number | null>(null);

  // Прокрутка к выбранному резерву
  useEffect(() => {
    if (selectedReserveId && scrollContainerRef.current) {
      const element = itemRefs.current.get(selectedReserveId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }, [selectedReserveId]);

  // Синхронизация с selectedReserveId
  useEffect(() => {
    if (selectedReserveId) {
      setOpenedReserveId(selectedReserveId);
    }
  }, [selectedReserveId]);

  const handleReserveClick = (reserveId: number) => {
    if (openedReserveId === reserveId) {
      setOpenedReserveId(null);
    } else {
      setOpenedReserveId(reserveId);
    }
    onReserveClick?.(reserveId);
  };

  if (isLoading || !reservesView?.length) {
    return null;
  }

  const hasOpenedReserve = openedReserveId !== null;
  const openedReserve = hasOpenedReserve
    ? reservesView.find((r) => r.id === openedReserveId)
    : null;

  // Минимальная высота горизонтальной ленты: 108px + paddingTop 10px + paddingBottom 10px = 128px
  // Плюс safe-area-inset-bottom для iOS
  const minListHeight = `calc(108px + 20px + env(safe-area-inset-bottom))`;

  return (
    <>
      {/* Горизонтальная лента со свернутыми карточками */}
      <div
        ref={scrollContainerRef}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#FFFFFF',
          borderTop: '1px solid #E5E5E5',
          paddingTop: '10px',
          paddingBottom: `calc(10px + env(safe-area-inset-bottom))`,
          zIndex: 100,
          overflowX: 'auto',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollSnapType: 'x mandatory',
          scrollPaddingLeft: '12px',
          scrollbarWidth: 'thin',
          scrollbarColor: '#C0C0C0 transparent',
          minHeight: minListHeight,
          maxHeight: 'calc(50vh - env(safe-area-inset-bottom))',
          boxSizing: 'border-box',
        }}
      >
        <Box
          display="flex"
          flexDirection="row"
          style={{
            minWidth: 'fit-content',
            gap: '12px',
            paddingRight: '12px',
          }}
        >
          {/* Спейсер для левого отступа, работает вместе со scroll-snap */}
          <div
            style={{
              flexShrink: 0,
              width: '0px',
              minWidth: '0px',
              marginLeft: '12px',
              marginRight: '-12px', // Компенсируем gap для левого отступа ровно 12px
              scrollSnapAlign: 'start',
            }}
            aria-hidden="true"
          />
          {reservesView.map((reserve) => (
            <div
              key={reserve.id}
              ref={(el) => {
                if (el) {
                  itemRefs.current.set(reserve.id, el);
                } else {
                  itemRefs.current.delete(reserve.id);
                }
              }}
              onClick={() => handleReserveClick(reserve.id)}
              style={{
                flexShrink: 0,
                width: '240px',
                minWidth: '240px',
                maxWidth: '240px',
                minHeight: '108px',
                scrollSnapAlign: 'start',
                scrollSnapStop: 'always',
                cursor: 'pointer',
                padding: '10px',
                borderRadius: '8px',
                border:
                  selectedReserveId === reserve.id ? '2px solid #009688' : '1px solid #E5E5E5',
                backgroundColor: selectedReserveId === reserve.id ? '#F0F9F8' : '#FFFFFF',
                transition: 'all 0.2s ease-in-out',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <ReserveHorizontalCard
                scheduleReserve={reserve}
                tablesSchedule={tablesSchedule}
                restaurantTz={restaurantTz}
                isSelected={selectedReserveId === reserve.id}
              />
            </div>
          ))}
        </Box>
      </div>

      {/* Bottom sheet с раскрытой карточкой (как при нажатии на стол) */}
      {openedReserve && (
        <SwipeableDrawer
          anchor="bottom"
          open={hasOpenedReserve}
          onClose={() => setOpenedReserveId(null)}
          onOpen={noop}
          disableSwipeToOpen={true}
          variant="temporary"
          PaperProps={{
            style: {
              maxHeight: 'calc(85vh - env(safe-area-inset-bottom))',
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              paddingBottom: 'env(safe-area-inset-bottom)',
            },
          }}
          BackdropProps={{
            style: {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            },
          }}
        >
          <Box
            className={classes.scrollableContent}
            paddingX="20px"
            paddingTop="12px"
            paddingBottom="max(24px, env(safe-area-inset-bottom))"
          >
            <Box
              width={40}
              height={4}
              borderRadius={2}
              bgcolor="rgba(0,0,0,0.2)"
              marginX="auto"
              marginBottom="16px"
            />
            <OpenedReserveWidget
              scheduleReserve={openedReserve}
              onCollapse={() => setOpenedReserveId(null)}
              onCancel={onCancel ? () => onCancel(openedReserve) : undefined}
              isCancelDisabled={isCancelDisabled}
              tablesSchedule={tablesSchedule}
              restaurantTz={restaurantTz}
              forcedOpen={true}
              // compact={false}
            />
          </Box>
        </SwipeableDrawer>
      )}
    </>
  );
};
